import React from 'react';
import { cssVar } from 'constants/theme';

import './index.scss';

const VIEW_WIDTH = 100;
const VIEW_HEIGHT = 28;

export interface SparklineProps {
  values: (number | null)[];
}

/**
 * Linha resumo das semanas. Sem eixo, sem tooltip, sem interação — por isso é SVG puro e
 * não ECharts (cinco cards = cinco canvas seria desperdício).
 */
export default function Sparkline({ values }: SparklineProps) {
  const numeric = values.filter((value): value is number => typeof value === 'number');
  if (!numeric.length) return null;

  const min = Math.min(...numeric);
  const max = Math.max(...numeric);
  // Série constante: divisor zero produziria NaN no path.
  const span = max - min || 1;
  const step = values.length > 1 ? VIEW_WIDTH / (values.length - 1) : 0;

  const points = values.map((value, index) => (
    typeof value === 'number'
      ? { x: index * step, y: VIEW_HEIGHT - ((value - min) / span) * VIEW_HEIGHT }
      : null
  ));

  // Buracos (null) interrompem o traço em vez de ligar pontos que não existem.
  const line = points.reduce((path, point, index) => {
    if (!point) return path;
    const command = index === 0 || !points[index - 1] ? 'M' : 'L';
    return `${path}${command}${point.x.toFixed(2)},${point.y.toFixed(2)} `;
  }, '').trim();

  const first = points.find(Boolean);
  const last = [...points].reverse().find(Boolean);
  // Área: o mesmo traço fechado até a base. Serve só de apoio visual — o dado é a linha.
  const area = first && last
    ? `${line} L${last.x.toFixed(2)},${VIEW_HEIGHT} L${first.x.toFixed(2)},${VIEW_HEIGHT} Z`
    : '';

  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path className="sparkline-area" d={area} fill={cssVar('text-link')} fillOpacity={0.12} stroke="none" />
      <path className="sparkline-line" d={line} fill="none" stroke={cssVar('text-link')} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      {last && <circle cx={last.x} cy={last.y} r={2} fill={cssVar('text-link')} vectorEffect="non-scaling-stroke" />}
    </svg>
  );
}
